import React from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

/**
 * Self-contained studio2-styled primitives for the redesigned create-session
 * modal. Deliberately NOT imported from SessionSettingsShared so the whole
 * create_session_v2 unit can be deleted in one go without touching anything
 * else. Visuals match the shared `.set-studio__card` / `.set-studio__field`
 * look via this dir's own scoped CSS (`.csv2-*`).
 */

interface CardProps {
  title: React.ReactNode;
  extra?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

export const Card: React.FC<CardProps> = ({
  title,
  extra,
  children,
  className,
}) => (
  <div className={['csv2-card', className].filter(Boolean).join(' ')}>
    <div className="csv2-card__head">
      <span className="csv2-card__title">{title}</span>
      {extra ? <span className="csv2-card__extra">{extra}</span> : null}
    </div>
    <div className="csv2-card__body">{children}</div>
  </div>
);

interface FieldProps {
  label: React.ReactNode;
  /** secondary explanatory line under the label */
  hint?: React.ReactNode;
  /** if set, shows an info icon next to the label with this tooltip */
  info?: React.ReactNode;
  children: React.ReactNode;
}

/** A single label-left / control-right settings row. */
export const Field: React.FC<FieldProps> = ({
  label,
  hint,
  info,
  children,
}) => (
  <div className="csv2-field">
    <div className="csv2-field__label">
      <span className="csv2-field__name">
        {label}
        {info ? (
          <Tooltip title={info}>
            <InfoCircleOutlined className="csv2-info" />
          </Tooltip>
        ) : null}
      </span>
      {hint ? <span className="csv2-field__hint">{hint}</span> : null}
    </div>
    <div className="csv2-field__control">{children}</div>
  </div>
);
